import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button, Modal, Select } from '@scripsteam/scrips-signal-ds';
import { TagSearchField } from '@/settings/components/tag-search-field';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  useDegreeSearch,
  useMedicalSchoolsSearch,
} from '@/settings/services';
import type { StaffNursingSchoolRow } from '@/types/settings-api';

/**
 * Add/edit dialog for a single Staff nursing-school qualification row
 * (PROD-788). Three required fields:
 *
 *   1. UNIVERSITY *   — typeahead (`useMedicalSchoolsSearch`)
 *   2. DEGREE *       — typeahead (`useDegreeSearch`)
 *   3. PASSING YEAR * — dropdown, current year ... current-100
 *
 * Layout mirrors `qualification-edit-dialog.tsx` (Practitioner) — same
 * primitives, same footer button shape, same Remove-with-confirm pattern.
 *
 * The dialog owns a draft `StaffNursingSchoolRow` and commits via
 * `onSave(next)`. Parent (`staff-professional-tab.tsx`) splices the draft
 * into the wrapper's `nursingSchool[]` array. Save is page-level — the
 * row diff/upsert is computed by `buildStaffProfessionalSaveBody` at
 * mutation time, not here.
 *
 * Wire-shape contract preserved:
 *   - `id: null` on new rows  → backend allocates.
 *   - `university` is the FULL `{Id, MedicalSchool}` master row (not just
 *     a GUID). Echoes verbatim back to the GET response.
 *   - `degree.degree` is the lowercase GUID from `useDegreeSearch`.
 *   - `degree.code` carries the full 5-field envelope; the save mapper
 *     backfills `description: null, shortName: null` if the typeahead
 *     omits them.
 *   - `isUpdated` is set by the save mapper based on the diff; the
 *     dialog leaves it `false` (irrelevant at edit time).
 */
export interface NursingSchoolEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` for an Add flow; existing row for an Edit flow. */
  initial: StaffNursingSchoolRow | null;
  onSave: (next: StaffNursingSchoolRow) => void;
  onRemove?: () => void;
}

function makeEmptyRow(): StaffNursingSchoolRow {
  return {
    id: null,
    university: { Id: '', MedicalSchool: '' },
    degree: {
      degree: '',
      code: {
        code: null,
        displayName: null,
        system: null,
        description: null,
        shortName: null,
      },
    },
    passingYear: 0,
    isUpdated: false,
  };
}

export function NursingSchoolEditDialog({
  open,
  onOpenChange,
  initial,
  onSave,
  onRemove,
}: NursingSchoolEditDialogProps) {
  const isEdit = !!initial && initial.id !== null;
  const [draft, setDraft] = useState<StaffNursingSchoolRow>(
    initial ?? makeEmptyRow(),
  );
  // Two-tap confirm for Remove — mirrors the Practitioner qualification
  // dialog (PR #124, @samer review). Single misclick has no undo once
  // the page-level Save fires.
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // Year-range computed inline per render (NOT memoised) — see
  // qualification-edit-dialog.tsx:106-112 for the rationale (Dec 31 →
  // Jan 1 long-running session).
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 101 }, (_, i) => currentYear - i);

  useEffect(() => {
    if (open) {
      setDraft(initial ?? makeEmptyRow());
      setConfirmingRemove(false);
    }
  }, [open, initial]);

  const isValid =
    draft.university.Id.length > 0 &&
    draft.degree.degree.length > 0 &&
    draft.passingYear > 0;

  const handleSave = () => {
    if (!isValid) {
      toast.error('University, degree, and passing year are required.');
      return;
    }
    onSave(draft);
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={isEdit ? 'Edit Nursing School' : 'Add Nursing School'}
      size="lg"
      footer={<><div className="flex items-center gap-2">
            <Button type="button" onClick={handleSave} disabled={!isValid}>
              Save
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
          {isEdit && onRemove && (
            <Button
              type="button"
              variant="tertiary"
              onClick={() => {
                if (confirmingRemove) {
                  onRemove();
                  onOpenChange(false);
                } else {
                  setConfirmingRemove(true);
                }
              }}
              className={
                confirmingRemove
                  ? 'bg-status-error text-text-inverse hover:bg-status-error/90'
                  : 'text-status-error hover:text-status-error hover:bg-status-error/10'
              }
            >
              {confirmingRemove ? 'Confirm Remove' : 'Remove'}
            </Button>
          )}</>}
      footerAlign="between"
    >
        

        <div data-slot="settings-dialog-body" className="space-y-4 py-2">
          <UniversityPicker
            value={{
              id: draft.university.Id || null,
              name: draft.university.MedicalSchool,
            }}
            onSelect={(picked) =>
              setDraft({
                ...draft,
                university: { Id: picked.id, MedicalSchool: picked.name },
              })
            }
          />

          <DegreePicker
            value={{
              id: draft.degree.degree || null,
              name: draft.degree.code.displayName ?? '',
            }}
            onSelect={(picked) =>
              setDraft({
                ...draft,
                degree: {
                  degree: picked.id,
                  code: picked.code,
                },
              })
            }
          />

          <div>
            <FieldLabel stacked>
              Passing year <span className="text-status-error">*</span>
            </FieldLabel>
            <Select
              value={draft.passingYear > 0 ? draft.passingYear.toString() : ''}
              onChange={(val) =>
                setDraft({ ...draft, passingYear: val ? Number(val) : 0 })
              }
              placeholder="Select passing year"
              options={yearOptions.map((y) => ({
                label: y.toString(),
                value: y.toString(),
              }))}
            />
          </div>
        </div>

        
      </Modal>
  );
}

// Pickers mirror `qualification-edit-dialog.tsx` exactly — the design Txt field
// with its result list under it (TagSearchField), same focus-to-open behaviour. Keeps the
// settings module's typeahead UX consistent across Practitioner + Staff
// Educational/Professional dialogs.

function UniversityPicker({
  value,
  onSelect,
}: {
  value: { id: string | null; name: string };
  onSelect: (picked: { id: string; name: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value.name);
  const debouncedSearch = useDebounce(search, 250);
  const { data: results = [], isLoading } =
    useMedicalSchoolsSearch(debouncedSearch);

  const options = useMemo(
    () =>
      results.map((r) => ({
        id: r.Id,
        label: r.MedicalSchool,
      })),
    [results],
  );

  const handleSelect = (id: string) => {
    const found = results.find((r) => r.Id === id);
    if (!found) return;
    setSearch(found.MedicalSchool);
    onSelect({ id: found.Id, name: found.MedicalSchool });
    setOpen(false);
  };

  return (
    <div>
      <FieldLabel stacked>
        University <span className="text-status-error">*</span>
      </FieldLabel>
      <TagSearchField
        value={search}
        placeholder="Enter nursing school"
        open={open}
        onOpenChange={setOpen}
        onSearchChange={setSearch}
        loading={isLoading}
        options={options}
        onSelect={handleSelect}
      />
    </div>
  );
}

function DegreePicker({
  value,
  onSelect,
}: {
  value: { id: string | null; name: string };
  onSelect: (picked: {
    id: string;
    name: string;
    code: StaffNursingSchoolRow['degree']['code'];
  }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value.name);
  const debouncedSearch = useDebounce(search, 250);
  const { data: results = [], isLoading } = useDegreeSearch(debouncedSearch);

  const options = useMemo(
    () =>
      results.map((r) => ({
        id: r.degree,
        label: r.code.displayName ?? r.code.code ?? r.degree,
        secondary: r.code.code ?? undefined,
      })),
    [results],
  );

  const handleSelect = (id: string) => {
    const found = results.find((r) => r.degree === id);
    if (!found) return;
    const name = found.code.displayName ?? found.code.code ?? '';
    setSearch(name);
    onSelect({
      id: found.degree,
      name,
      code: {
        code: found.code.code,
        displayName: found.code.displayName,
        system: found.code.system,
        description: found.code.description ?? null,
        shortName: found.code.shortName ?? null,
      },
    });
    setOpen(false);
  };

  return (
    <div>
      <FieldLabel stacked>
        Degree <span className="text-status-error">*</span>
      </FieldLabel>
      <TagSearchField
        value={search}
        placeholder="Enter degree"
        open={open}
        onOpenChange={setOpen}
        onSearchChange={setSearch}
        loading={isLoading}
        options={options}
        onSelect={handleSelect}
      />
    </div>
  );
}
